import {
  type ReactNode,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import dayjs from "dayjs";
import {
  useLoaderData,
  useRouteContext,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { useDialog } from "@/hooks/useDialog";
import { GenerationContext } from "./generationContext";
import {
  readPersistedSelections,
  writePersistedSelections,
} from "./generationStorage";
import type { GenerationData, GenerationStep } from "@/types/generation.types";
import type { GeneratedQuestionsResult } from "@/types/apis/naniexpert";
import { getUserRecordsCount } from "@/apis/naniexpert/services/member";
import { useGenerateContentSearchPolling } from "@/apis/naniexpert/hooks/genai";
import { generateContentByParams } from "@/apis/naniexpert/services/genai";
import {
  AI_QUESTION_GENERATION_MAX_COUNT,
  GENERATION_STEP,
  LABELS_BY_EDU,
  IS_ENABLE_GENERATION_COUNT_LIMIT,
} from "@/utils/constants";
import {
  getEduOptions,
  getSubjectsByEdu,
  getVolumesBySubject,
  getChaptersByVolume,
  getSubChaptersByChapter,
  getSubjectCodeWithoutEdu,
  pickValidOption,
} from "@/utils/dataHelpers";
import {
  GenerationFormatError,
  getOptionNameByCode,
  parseGenerateContentResult,
  validateGenerationFormat,
} from "@/utils/generation.helper";
import { sentryCaptureIfUnreported } from "@/utils/sentry.helper";
import { handleApiError } from "@/utils/handleApiError";
import ReactGA from "react-ga4";
import type { EduType } from "@/types/common.types";
import { useGetUserRecordsCountQuery } from "@/apis/naniexpert/hooks/member";
import { useAuthContext } from "@/providers/authProvider/authContext";

const EMPTY_GENERATION_DATA: GenerationData = {
  eduContent: [],
  subjectContent: {},
  volumeContent: {},
  chapterContent: {},
  subChapterContent: {},
};

/**
 * 選項階層，由上到下；設定某一層時會清空其下所有層級
 */
const SELECTION_LEVELS = [
  "edu",
  "subject",
  "volume",
  "chapter",
  "subChapter",
] as const;

type SelectionLevel = (typeof SELECTION_LEVELS)[number];

interface RawSelections extends Record<SelectionLevel, string | null> {
  customText: string;
}

const initRawSelections = () => {
  const persisted = readPersistedSelections();

  return {
    edu: persisted?.selectedEdu ?? null,
    subject: persisted?.selectedSubject ?? null,
    volume: persisted?.selectedVolume ?? null,
    chapter: persisted?.selectedChapter ?? null,
    subChapter: persisted?.selectedSubChapter ?? null,
    customText: persisted?.customText ?? "",
  };
};

export const GenerationProvider = ({ children }: { children: ReactNode }) => {
  const loaderData = useLoaderData({ from: "/" });
  const { generationDataError } = useRouteContext({ from: "/" });
  const { openDialog } = useDialog();
  const navigate = useNavigate();
  const router = useRouter();
  const { user } = useAuthContext();
  const { refetch: refetchGetUserRecordsCount } = useGetUserRecordsCountQuery({
    enabled: !!user,
  });

  useEffect(() => {
    if (generationDataError) {
      openDialog({
        title: "查無章節資料",
        content: "目前沒有可用的章節資料，請稍後再試。",
        confirmText: "確認",
        onConfirm: () => router.invalidate(),
        isShowCloseIcon: false,
      });
    }
  }, [generationDataError, openDialog, router]);

  const generationData = loaderData ?? EMPTY_GENERATION_DATA;

  const [rawSelections, setRawSelections] =
    useState<RawSelections>(initRawSelections);
  const { customText } = rawSelections;
  const [step, setStep] = useState<GenerationStep>(GENERATION_STEP.SELECT);
  const [generationResult, setGenerationResult] =
    useState<GeneratedQuestionsResult | null>(null);
  const [pollingUid, setPollingUid] = useState<string | null>(null);

  const { data: generateContentSearchResponse } =
    useGenerateContentSearchPolling(pollingUid);

  const eduOptions = useMemo(
    () => getEduOptions(generationData),
    [generationData]
  );
  const selectedEdu = pickValidOption(rawSelections.edu, eduOptions);

  const subjectOptions = useMemo(
    () => getSubjectsByEdu(generationData, selectedEdu),
    [generationData, selectedEdu]
  );
  const selectedSubject = pickValidOption(
    rawSelections.subject,
    subjectOptions
  );

  const volumeOptions = useMemo(
    () => getVolumesBySubject(generationData, selectedSubject),
    [generationData, selectedSubject]
  );
  const selectedVolume = pickValidOption(rawSelections.volume, volumeOptions);

  const chapterOptions = useMemo(
    () => getChaptersByVolume(generationData, selectedSubject, selectedVolume),
    [generationData, selectedSubject, selectedVolume]
  );
  const selectedChapter = pickValidOption(
    rawSelections.chapter,
    chapterOptions
  );

  const subChapterOptions = useMemo(
    () =>
      getSubChaptersByChapter(
        generationData,
        selectedSubject,
        selectedVolume,
        selectedChapter
      ),
    [generationData, selectedSubject, selectedVolume, selectedChapter]
  );
  const selectedSubChapter = pickValidOption(
    rawSelections.subChapter,
    subChapterOptions
  );

  const setSelection = useCallback(
    (level: SelectionLevel, value: string | null) => {
      const selectedByLevel: Record<SelectionLevel, string | null> = {
        edu: selectedEdu,
        subject: selectedSubject,
        volume: selectedVolume,
        chapter: selectedChapter,
        subChapter: selectedSubChapter,
      };

      // 按下已選選項時，不需要重置下方層級
      if (value === selectedByLevel[level]) {
        return;
      }

      // 重置下方層級
      setRawSelections((prev) => {
        const next: RawSelections = { ...prev, [level]: value };
        const changedLevelIndex = SELECTION_LEVELS.indexOf(level);

        SELECTION_LEVELS.forEach((currentLevel, currentLevelIndex) => {
          const isLowerThanChangedLevel = currentLevelIndex > changedLevelIndex;

          if (isLowerThanChangedLevel) {
            next[currentLevel] = null;
          }
        });

        return next;
      });
    },
    [
      selectedEdu,
      selectedSubject,
      selectedVolume,
      selectedChapter,
      selectedSubChapter,
    ]
  );

  const setSelectedEdu = useCallback(
    (edu: string | null) => setSelection("edu", edu),
    [setSelection]
  );

  const setSelectedSubject = useCallback(
    (subject: string | null) => setSelection("subject", subject),
    [setSelection]
  );

  const setSelectedVolume = useCallback(
    (volume: string | null) => setSelection("volume", volume),
    [setSelection]
  );

  const setSelectedChapter = useCallback(
    (chapter: string | null) => setSelection("chapter", chapter),
    [setSelection]
  );

  const setSelectedSubChapter = useCallback(
    (subChapter: string | null) => setSelection("subChapter", subChapter),
    [setSelection]
  );

  const setCustomText = useCallback((text: string) => {
    setRawSelections((prev) => ({ ...prev, customText: text }));
  }, []);

  // 任一選項變動即同步至 sessionStorage
  useEffect(() => {
    writePersistedSelections({
      selectedEdu,
      selectedSubject,
      selectedVolume,
      selectedChapter,
      selectedSubChapter,
      customText,
    });
  }, [
    selectedEdu,
    selectedSubject,
    selectedVolume,
    selectedChapter,
    selectedSubChapter,
    customText,
  ]);

  const isValidSubChapter = useCallback(() => {
    if (!subChapterOptions || subChapterOptions.length === 0) {
      return true;
    }

    return !!selectedSubChapter;
  }, [selectedSubChapter, subChapterOptions]);

  const checkHasRemainingCount = useCallback(async () => {
    if (!IS_ENABLE_GENERATION_COUNT_LIMIT) {
      return true;
    }

    try {
      const usedCountData = await getUserRecordsCount();
      const usedCount = usedCountData?.count ?? 0;
      const remainingCount = AI_QUESTION_GENERATION_MAX_COUNT - usedCount;

      if (remainingCount <= 0) {
        openDialog({
          title: "使用次數已達上限",
          content: `您已達使用次數的上限，請於隔日 ${dayjs(
            usedCountData.startTime
          ).format("HH:mm")} 後繼續使用`,
          confirmText: "離開",
          isShowCloseIcon: false,
        });
        return false;
      }

      return true;
    } catch (error) {
      handleApiError(error, (err) => {
        console.error("getUserRecordsCount error:", err);
        openDialog({
          title: "取得使用次數失敗",
          content: "請稍後再試。",
          confirmText: "確認",
          onConfirm: () => navigate({ to: "/" }),
          isShowCloseIcon: false,
        });
      });

      return false;
    }
  }, [openDialog, navigate]);

  const sendGenerationEventToGA = useCallback(() => {
    try {
      const eduName = getOptionNameByCode(eduOptions, selectedEdu);
      const subjectName = getOptionNameByCode(subjectOptions, selectedSubject);
      const volumeName = getOptionNameByCode(volumeOptions, selectedVolume);
      const chapterName = getOptionNameByCode(chapterOptions, selectedChapter);
      const subChapterName = getOptionNameByCode(
        subChapterOptions,
        selectedSubChapter
      );

      ReactGA.event("generate_questions", {
        edu: eduName,
        subject: `${eduName} - ${subjectName}`,
        volume: `${eduName} - ${subjectName} - ${volumeName}`,
        chapter: `${eduName} - ${subjectName} - ${volumeName} - ${chapterName}`,
        sub_chapter: `${eduName} - ${subjectName} - ${volumeName} - ${chapterName} - ${subChapterName}`,
        has_custom_text: String(customText.length > 0),
      });
    } catch (error) {
      console.error("sendGenerationEventToGA error:", error);
    }
  }, [
    selectedEdu,
    selectedSubject,
    selectedVolume,
    selectedChapter,
    selectedSubChapter,
    customText,
    eduOptions,
    subjectOptions,
    volumeOptions,
    chapterOptions,
    subChapterOptions,
  ]);

  const sendGenerationErrorToSentry = useCallback(
    (error: Error) => {
      try {
        const generationParams = {
          edu: selectedEdu,
          subject: selectedSubject,
          volume: selectedVolume,
          chapter: selectedChapter,
          subChapter: selectedSubChapter,
          customText,
        };

        // 區分格式驗證錯誤與 API 錯誤，使用不同 Sentry tag 分類追蹤
        const isFormatError = error instanceof GenerationFormatError;

        sentryCaptureIfUnreported(error, {
          tag: isFormatError
            ? "generation_format_validation_error"
            : "generateContent_error",
          extras: {
            ...generationParams,
            ...(isFormatError && {
              invalidRawResponse: (error as GenerationFormatError)
                .invalidRawResponse,
            }),
          },
        });
      } catch (error) {
        console.error("sendGenerationErrorToSentry error:", error);
      }
    },
    [
      selectedEdu,
      selectedSubject,
      selectedVolume,
      selectedChapter,
      selectedSubChapter,
      customText,
    ]
  );

  const handleGenerate = useCallback(async () => {
    if (
      !selectedEdu ||
      !selectedSubject ||
      !selectedVolume ||
      !selectedChapter ||
      !isValidSubChapter()
    ) {
      const missFields = [];

      if (!selectedEdu) {
        missFields.push("學制");
      }
      if (!selectedSubject) {
        missFields.push("科目");
      }
      if (!selectedVolume) {
        missFields.push("冊次");
      }
      if (!selectedChapter) {
        missFields.push("章節");
      }

      if (!isValidSubChapter()) {
        missFields.push("小節");
      }

      openDialog({
        title: "請完成所有必填選項",
        content: `請完成所有必填選項：${missFields.join("、")}`,
        confirmText: "確認",
        isShowCloseIcon: false,
      });

      return;
    }

    const hasRemainingCount = await checkHasRemainingCount();

    if (!hasRemainingCount) {
      return;
    }

    sendGenerationEventToGA();

    setStep(GENERATION_STEP.GENERATING);

    try {
      const payload = {
        educode: selectedEdu ?? "",
        subjectcode: getSubjectCodeWithoutEdu(selectedSubject ?? ""),
        volumecode: selectedVolume ?? "",
        chaptercode: selectedChapter ?? "",
        subchaptercode: selectedSubChapter ?? "",
        customText,
      };

      const { uid } = await generateContentByParams(payload);

      refetchGetUserRecordsCount();

      setPollingUid(uid);
    } catch (error) {
      setStep(GENERATION_STEP.SELECT);

      handleApiError(error, (err) => {
        console.error("handleGenerate error:", err);
        sendGenerationErrorToSentry(err);

        openDialog({
          title: "生成失敗",
          content: err.message || "題目生成失敗，請稍後再試。",
          confirmText: "確認",
          isShowCloseIcon: false,
        });
      });
    }
  }, [
    selectedEdu,
    selectedSubject,
    selectedVolume,
    selectedChapter,
    selectedSubChapter,
    customText,
    isValidSubChapter,
    checkHasRemainingCount,
    openDialog,
    sendGenerationErrorToSentry,
    sendGenerationEventToGA,
    refetchGetUserRecordsCount,
  ]);

  const getParsedGenerationResult = useCallback(() => {
    if (!generateContentSearchResponse) {
      throw new Error(
        "[getParsedGenerationResult] generateContentSearchResponse is empty"
      );
    }

    const parsedResult = parseGenerateContentResult(
      generateContentSearchResponse,
      LABELS_BY_EDU[selectedEdu as EduType]
    );

    const isValidFormat = validateGenerationFormat(parsedResult);

    if (!isValidFormat) {
      sendGenerationErrorToSentry(
        new GenerationFormatError(
          generateContentSearchResponse?.geminiResponse?.text ?? "",
          "[getParsedGenerationResult] Generation successfully but is invalid format"
        )
      );
    }

    return parsedResult;
  }, [sendGenerationErrorToSentry, selectedEdu, generateContentSearchResponse]);

  /**
   * 拿到輪詢結果後，進行後續流程
   */
  useEffect(() => {
    if (pollingUid && generateContentSearchResponse?.gradestatus === "Finish") {
      try {
        const parsedResult = getParsedGenerationResult();

        // eslint-disable-next-line
        setGenerationResult(parsedResult);
        setStep(GENERATION_STEP.RESULT);
        setPollingUid(null);
      } catch (error) {
        console.error("useEffect polling error:", error);
        sendGenerationErrorToSentry(error as Error);
        setStep(GENERATION_STEP.SELECT);
        setPollingUid(null);
      }
    }
  }, [
    pollingUid,
    generateContentSearchResponse,
    selectedEdu,
    sendGenerationErrorToSentry,
    getParsedGenerationResult,
  ]);

  const contextValue = useMemo(
    () => ({
      step,
      setStep,
      generationResult,
      selectedEdu,
      selectedSubject,
      selectedVolume,
      selectedChapter,
      selectedSubChapter,
      customText,
      setSelectedEdu,
      setSelectedSubject,
      setSelectedVolume,
      setSelectedChapter,
      setSelectedSubChapter,
      setCustomText,
      eduOptions,
      subjectOptions,
      volumeOptions,
      chapterOptions,
      subChapterOptions,
      handleGenerate,
    }),
    [
      step,
      setStep,
      generationResult,
      selectedEdu,
      selectedSubject,
      selectedVolume,
      selectedChapter,
      selectedSubChapter,
      customText,
      setSelectedEdu,
      setSelectedSubject,
      setSelectedVolume,
      setSelectedChapter,
      setSelectedSubChapter,
      setCustomText,
      eduOptions,
      subjectOptions,
      volumeOptions,
      chapterOptions,
      subChapterOptions,
      handleGenerate,
    ]
  );

  return (
    <GenerationContext.Provider value={contextValue}>
      {generationDataError ? null : children}
    </GenerationContext.Provider>
  );
};
